import { toggleAttribute, toggleHidden } from 'src/utils/dom-toggle';
import { Controller } from '@hotwired/stimulus';

/**
 * Fetches content as markdown and copies it to the clipboard.
 */
export default class CopyMarkdownController extends Controller {
  public static values = {
    url: String,
  };

  private declare urlValue: string;

  private declare animationTimeout: number | undefined;

  public connect(): void {
    if (
      navigator &&
      'clipboard' in navigator &&
      typeof navigator.clipboard.writeText === 'function'
    ) {
      toggleHidden(this.element, false);
    }
  }

  public disconnect(): void {
    toggleHidden(this.element, true);
    this.clearFeedbackState();
  }

  public onCopy(): void {
    if (this.element.hasAttribute('data-copying')) {
      return;
    }

    this.clearFeedbackState();
    toggleAttribute('data-copying', this.element, true);
    toggleAttribute('disabled', this.element, true);

    fetch(this.urlValue, {
      credentials: 'same-origin',
      headers: {
        Accept: 'text/markdown',
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to fetch markdown (${response.status})`);
        }

        return response.text();
      })
      .then((text) => navigator.clipboard.writeText(text))
      .then(() => {
        toggleAttribute('data-copying', this.element, false);
        toggleAttribute('data-copied', this.element, true);
        this.scheduleFeedbackReset();
      })
      .catch(() => {
        toggleAttribute('data-copying', this.element, false);
        toggleAttribute('data-failed', this.element, true);
        this.scheduleFeedbackReset();
      })
      .finally(() => {
        toggleAttribute('disabled', this.element, false);
      });
  }

  private scheduleFeedbackReset(): void {
    clearTimeout(this.animationTimeout);
    this.animationTimeout = setTimeout(() => {
      this.clearFeedbackState();
    }, 5 * 1000) as unknown as number;
  }

  private clearFeedbackState(): void {
    clearTimeout(this.animationTimeout);
    toggleAttribute('data-copying', this.element, false);
    toggleAttribute('data-copied', this.element, false);
    toggleAttribute('data-failed', this.element, false);
  }
}
